/**
 * ICU MessageFormat for i18next — G6.
 *
 *   "ICU MessageFormat for every string. Plural categories (Italian and Spanish differ from
 *    English), gender, ordinals and nested selects are handled by the format, NEVER by
 *    string concatenation or by `if count == 1`."
 *
 * WHY THIS IS HAND-WRITTEN RATHER THAN `i18next-icu`.
 *
 * `i18next-icu` is a thin wrapper over `intl-messageformat` and was the obvious first
 * choice. Under i18next 25 its `parse` hook did not take effect: messages resolved from the
 * catalogue but came back with their ICU syntax intact, so `grid.rowCount` rendered as the
 * literal `{count, plural, =0 {No rows} one {# row} other {# rows}}`. A plural that renders
 * its own source is a defect a planner sees, and G6 makes plural handling the format's job,
 * so the layer that does it cannot be one whose compatibility we are guessing at.
 *
 * `intl-messageformat` (BSD-3-Clause) is already on the design/09 §10.1 register via the
 * G4 i18n row, and it is the engine `i18next-icu` wraps. Talking to it directly removes a
 * dependency, removes a version-compatibility surface, and makes the failure mode above
 * impossible: if `parse` is not called, every message in the product renders as ICU source
 * and `src/i18n/i18n.test.ts` fails.
 */

import { IntlMessageFormat, type Formatters } from 'intl-messageformat';
import type { i18n as I18nInstance, Newable, ThirdPartyModule } from 'i18next';
import { displayNumberFormat } from '../lib/format';

type ParseOptions = Record<string, unknown> & {
  /** i18next passes the interpolation values on the options object. */
  readonly interpolation?: Record<string, unknown>;
};

export interface IcuFormatOptions {
  /**
   * What to do with a message whose ICU syntax does not parse. In development the message is
   * a hard failure, because a broken plural silently falling back to its source is how a
   * mistranslated markdown-trigger label reaches production — which G6 calls "a commercial
   * error, not a cosmetic one".
   */
  readonly throwOnParseError?: boolean;
}

/**
 * Compiled messages are cached by `locale + message`. Compilation is the expensive part of
 * ICU formatting, and a planning grid formats thousands of cells per frame — recompiling
 * `grid.rowCount` on every one would be measurable against G2 §2.1's 150 ms budget.
 */
const cache = new Map<string, IntlMessageFormat>();

/**
 * Numbers in a catalogue string — `{count, number}` and the `#` of a plural — group their
 * thousands ALWAYS, like every other figure on screen. `Intl` alone groups it-IT and es-419 only
 * from five digits (CLDR `minimumGroupingDigits: 2`), so «7661» would sit beside «62.802»
 * (owner decision 2026-10-11). `intl-messageformat` builds both through `getNumberFormat`, so
 * this one override covers them; an explicit `useGrouping` in an ICU skeleton still wins. The
 * date and plural-rule formatters are `intl-messageformat`'s own.
 */
function memoised<A extends unknown[], R>(make: (...args: A) => R): (...args: A) => R {
  const made = new Map<string, R>();
  return (...args: A): R => {
    const key = JSON.stringify(args);
    let value = made.get(key);
    if (value === undefined) {
      value = make(...args);
      made.set(key, value);
    }
    return value;
  };
}

const FORMATTERS: Formatters = {
  getNumberFormat: (locales, options) => displayNumberFormat(
    Array.isArray(locales) ? (locales[0] ?? 'en-GB') : (locales ?? 'en-GB'),
    options,
  ),
  getDateTimeFormat: memoised((...args: ConstructorParameters<typeof Intl.DateTimeFormat>) => new Intl.DateTimeFormat(...args)),
  getPluralRules: memoised((...args: ConstructorParameters<typeof Intl.PluralRules>) => new Intl.PluralRules(...args)),
};

export class IcuFormat {
  static readonly type = 'i18nFormat' as const;

  private locales: string[] = [];

  private throwOnParseError = false;

  init(i18next: I18nInstance, options: { i18nFormat?: IcuFormatOptions } = {}): void {
    this.locales = [i18next.language, ...(i18next.languages ?? [])].filter(Boolean);
    this.throwOnParseError = options.i18nFormat?.throwOnParseError === true;
  }

  parse(
    res: string,
    options: ParseOptions,
    lng: string,
    _ns: string,
    key: string,
  ): string {
    if (typeof res !== 'string' || res === '') return res;
    // Nothing to format: no placeholder, no plural, no select.
    if (!res.includes('{')) return res;

    const locale = lng || this.locales[0] || 'en-GB';
    const cacheKey = `${locale} ${res}`;

    let compiled = cache.get(cacheKey);
    if (!compiled) {
      try {
        compiled = new IntlMessageFormat(res, locale, undefined, { formatters: FORMATTERS });
      } catch (error) {
        if (this.throwOnParseError) {
          throw new Error(
            `ICU message "${key}" does not parse in ${locale}: ${(error as Error).message}. `
            + 'The message is in locale/*/ and its ICU syntax is wrong — most often an unbalanced '
            + 'brace, or a plural arm keyword that is not one of zero/one/two/few/many/other.',
            { cause: error },
          );
        }
        return res;
      }
      cache.set(cacheKey, compiled);
    }

    // i18next puts the values directly on `options`. Its own reserved keys are stripped so
    // they cannot collide with a placeholder name.
    const values: Record<string, unknown> = {};
    for (const [name, value] of Object.entries(options)) {
      if (RESERVED.has(name)) continue;
      values[name] = value;
    }

    try {
      const formatted = compiled.format(values);
      return typeof formatted === 'string' ? formatted : String(formatted);
    } catch (error) {
      if (this.throwOnParseError) {
        throw new Error(
          `ICU message "${key}" could not be formatted in ${locale}: ${(error as Error).message}. `
          + 'This is usually a placeholder the caller did not supply — the ICU message names an '
          + 'argument that is not in the values object.',
          { cause: error },
        );
      }
      return res;
    }
  }

  /** Clears the compiled-message cache. Used when the language changes. */
  static clearCache(): void {
    cache.clear();
  }
}

/** i18next's own option names, which are never ICU placeholders. */
const RESERVED = new Set([
  'lng', 'lngs', 'fallbackLng', 'ns', 'defaultValue', 'keySeparator', 'nsSeparator',
  'returnObjects', 'returnDetails', 'joinArrays', 'postProcess', 'interpolation',
  'skipInterpolation', 'replace', 'context', 'count_plural', 'ordinal',
]);

/** Typed for `i18next.use()`. */
export const icuFormat = IcuFormat as unknown as Newable<ThirdPartyModule>;
